/**
 * Main game loop
 * Updates all time-based systems
 */

export class GameLoop {
  private lastUpdate: number = Date.now();
  private animationFrameId: number | null = null;
  private isRunning: boolean = false;
  private updateCallbacks: Set<(deltaTime: number) => void> = new Set();

  /**
   * Register a callback to be called every frame
   */
  register(callback: (deltaTime: number) => void): () => void {
    this.updateCallbacks.add(callback);

    // Return unregister function
    return () => {
      this.updateCallbacks.delete(callback);
    };
  }

  /**
   * Start the game loop
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastUpdate = Date.now();
    this.loop();
  }

  /**
   * Stop the game loop
   */
  stop(): void {
    this.isRunning = false;
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Main loop - runs every frame
   */
  private loop = (): void => {
    if (!this.isRunning) return;

    const now = Date.now();
    const deltaTime = (now - this.lastUpdate) / 1000; // Convert to seconds
    this.lastUpdate = now;

    // Update all registered callbacks
    this.updateCallbacks.forEach((callback) => {
      try {
        callback(deltaTime);
      } catch (error) {
        console.error('Error in game loop callback:', error);
      }
    });

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame(this.loop);
  };

  /**
   * Check if loop is running
   */
  get running(): boolean {
    return this.isRunning;
  }
}

// Singleton instance
export const gameLoop = new GameLoop();
